import { isNavigationFailure, NavigationFailureType, type HistoryState } from 'vue-router'

/**
 * Navegar para a tela em que a pessoa já está é sucesso, não falha.
 *
 * O vue-router 4 não rejeita `push` para a rota vigente: ele **resolve** com um
 * `NavigationFailure` do tipo `duplicated`. Quem só olha "retornou algo?" lê
 * isso como erro — foi o que matou o C7 da rodada 2. O agente propôs abrir
 * `/holidays` com a pessoa já em `/holidays`, o turno concluiu com sucesso no
 * backend, e mesmo assim a conversa exibiu "Não conseguimos concluir essa
 * solicitação agora".
 *
 * A `duplicated` é o único tipo tratado como sucesso. `aborted` e `cancelled`
 * continuam sendo falha: numa, uma guarda barrou a rota; na outra, outra
 * navegação atropelou esta. Nos dois casos a tela pedida NÃO está na frente da
 * pessoa, então dizer que deu certo seria mentir.
 *
 * O `state` é o motivo de a duplicada não ser simplesmente ignorada: é por ele
 * que a tela recebe o rascunho do handoff (`identificador_rota`,
 * `rascunho_opaco`). Numa navegação de verdade quem aplica é o router; na
 * duplicada não há navegação, então o host aplica à mão — senão a tela abre
 * "certa" e vazia, que é a pior das saídas: silenciosa.
 */

export interface AlvoDeNavegacaoDoHost {
  path: string
  state?: HistoryState
}

export type EmpurrarRota = (alvo: AlvoDeNavegacaoDoHost) => Promise<unknown> | unknown
export type AplicarEstadoDaRota = (estado: HistoryState) => void

/** Funde o state do handoff no history sem descartar o que já estava lá. */
export function aplicarEstadoNoHistorico (estado: HistoryState): void {
  if (typeof window === 'undefined' || !window.history) return
  window.history.replaceState({ ...window.history.state, ...estado }, '')
}

/**
 * Empurra a rota e devolve o que `executarNavegacaoHandoff` espera: valor falsy
 * quando a tela pedida está na frente da pessoa, a falha original quando não.
 */
export async function navegarPeloHost (
  alvo: AlvoDeNavegacaoDoHost,
  empurrar: EmpurrarRota,
  aplicarEstado: AplicarEstadoDaRota = aplicarEstadoNoHistorico
): Promise<unknown> {
  const falha = await empurrar(alvo)
  if (!falha || !isNavigationFailure(falha, NavigationFailureType.duplicated)) return falha

  if (alvo.state) aplicarEstado(alvo.state)
  return undefined
}
